import { useEffect, useState } from "react";
import { Globe, Inbox, Mailbox, PenSquare, Settings } from "lucide-react";

import type { NavigateFn } from "~/app/navigate-context";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Kbd } from "~/components/ui/kbd";

/** Keyboard-selected row: 3px ember ring (DESIGN.md focus), not a harvest KPI chip. */
const EMBER_FOCUS =
  "data-[selected=true]:bg-selected data-[selected=true]:text-foreground";

interface CommandPaletteEntity {
  label: string;
  secondary?: string;
  onSelect: () => void;
}

interface CommandPaletteAction {
  label: string;
  onSelect: () => void;
}

interface CommandPaletteProps {
  navigate: NavigateFn;
  entities?: {
    domains?: CommandPaletteEntity[];
    mailboxes?: CommandPaletteEntity[];
    conversations?: CommandPaletteEntity[];
  };
  actions?: CommandPaletteAction[];
}

const PAGE_GROUPS = [
  {
    heading: "Mail",
    items: [
      { title: "Inbox", href: "/mail", Icon: Inbox },
      { title: "Compose", href: "/mail?compose=1", Icon: PenSquare },
    ],
  },
  {
    heading: "Infrastructure",
    items: [
      { title: "Domains", href: "/domains", Icon: Globe },
      { title: "Mailboxes", href: "/mailboxes", Icon: Mailbox },
    ],
  },
  {
    heading: "Account",
    items: [{ title: "Settings", href: "/settings", Icon: Settings }],
  },
] as const;

export function CommandPalette({ navigate, entities, actions }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function go(href: string) {
    setOpen(false);
    navigate(href);
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Command Palette"
      description="Search entities, navigate, and run actions."
      showCloseButton={false}
    >
      <CommandInput placeholder="Search…" className="focus-visible:shadow-focus" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Page navigation group */}
        {PAGE_GROUPS.map((group) => (
          <CommandGroup key={group.heading} heading={group.heading}>
            {group.items.map(({ title, href, Icon }) => (
              <CommandItem key={href} value={title} onSelect={() => go(href)} className={EMBER_FOCUS}>
                <Icon />
                {title}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}

        {/* Domain entities group */}
        {entities?.domains && entities.domains.length > 0 && (
          <CommandGroup heading="Domains">
            {entities.domains.map((entity) => (
              <CommandItem
                key={entity.label}
                value={entity.label}
                onSelect={entity.onSelect}
                className={EMBER_FOCUS}
              >
                {entity.label}
                {entity.secondary && (
                  <span className="ml-auto text-xs text-foreground-muted">{entity.secondary}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Mailbox entities group */}
        {entities?.mailboxes && entities.mailboxes.length > 0 && (
          <CommandGroup heading="Mailboxes">
            {entities.mailboxes.map((entity) => (
              <CommandItem
                key={entity.label}
                value={entity.label}
                onSelect={entity.onSelect}
                className={EMBER_FOCUS}
              >
                {entity.label}
                {entity.secondary && (
                  <span className="ml-auto text-xs text-foreground-muted">{entity.secondary}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Conversation entities group */}
        {entities?.conversations && entities.conversations.length > 0 && (
          <CommandGroup heading="Conversations">
            {entities.conversations.map((entity) => (
              <CommandItem
                key={entity.label}
                value={entity.label}
                onSelect={entity.onSelect}
                className={EMBER_FOCUS}
              >
                <div className="flex flex-col gap-0.5 flex-1">
                  <span>{entity.label}</span>
                  {entity.secondary && (
                    <span className="text-xs text-foreground-muted">{entity.secondary}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Actions group */}
        {actions && actions.length > 0 && (
          <CommandGroup heading="Actions">
            {actions.map((action) => (
              <CommandItem
                key={action.label}
                value={action.label}
                onSelect={action.onSelect}
                className={EMBER_FOCUS}
              >
                {action.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>

      {/* Footer with keyboard shortcuts */}
      <div className="border-t bg-surface-2 px-3 py-2 text-xs text-foreground-muted flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Kbd>↑↓</Kbd>
            <span>Navigate</span>
          </div>
          <div className="flex items-center gap-1">
            <Kbd>↵</Kbd>
            <span>Select</span>
          </div>
          <div className="flex items-center gap-1">
            <Kbd>Esc</Kbd>
            <span>Close</span>
          </div>
        </div>
      </div>
    </CommandDialog>
  );
}
